import React, { ComponentProps, ReactNode } from 'react';

import styledC from 'styled-components';

import Avatar from '@adsk/alloy-react-avatar';
import {
  isGroupHeader,
  RenderMenuItem,
  VirtualizedMenuGroupHeader,
  VirtualizedMenuItem,
} from '@adsk/alloy-react-dropdown';
import HighlightText from '@adsk/alloy-react-highlight-text';
import Theme, { flattenStyle } from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { getFallbackIconRenderer, isMemberItem } from '../helpers/avatar';
import { isSubtextSearchable } from '../helpers/utils';
import { ItemWithGroup } from '../types';

const AssigneePickerVirtualizedMenuItem = styledC(VirtualizedMenuItem)`
  align-items: center;
`;

export const processMenuItemText = (
  item: ItemWithGroup,
  inputValue?: string,
) => {
  const { label, subtext } = item;
  const renderText = (text: ReactNode) => {
    if (inputValue?.trim() && typeof text === 'string') {
      return <HighlightText text={text} matched={inputValue.trim()} />;
    }
    return text;
  };
  return {
    label,
    subtext,
    renderedLabel: renderText(label),
    renderedSubtext: isSubtextSearchable(item.group)
      ? renderText(subtext)
      : subtext,
  };
};

// This callback is invoked directly for each menu item, so compiler-added
// hooks would change the caller's hook count when the item count changes.
export const AssigneePickerMenuItem: RenderMenuItem<
  ItemWithGroup & { inputValue?: string }
> = (itemProps) => {
  'use no memo';

  const { inputValue, ...restProps } = itemProps;
  if (isGroupHeader(itemProps)) {
    const { style, ...groupHeaderProps } = restProps;
    // RenderMenuItem types every item as StylableComponent<HTMLLIElement>, but
    // the group-header branch renders a paragraph-based component. The dropdown
    // also injects runtime-only props (allowCollapsibleGroups, expanded, ...)
    // that must keep flowing through the spread, so we assert the header's own
    // props. The only shape difference is the DOM element the (identical) event
    // handlers are bound to, which is a no-op at runtime.
    return (
      <VirtualizedMenuGroupHeader
        {...(groupHeaderProps as ComponentProps<
          typeof VirtualizedMenuGroupHeader
        >)}
        style={flattenStyle(style)}
      >
        {itemProps.label}
      </VirtualizedMenuGroupHeader>
    );
  }

  const renderFallbackIcon = getFallbackIconRenderer(itemProps.item.group);
  const isMember = isMemberItem(itemProps.item.group);

  const iconProps: Partial<ComponentProps<typeof VirtualizedMenuItem>> = {
    renderLeftComponent: () => (
      <Avatar
        image={itemProps.item.imageUrl}
        size={Avatar.SIZES.MEDIUM_32}
        name={itemProps.item.label as string}
        style={{
          marginRight: Theme.spacing.XS,
          ...(!isMember ? { backgroundColor: Theme.colors.charcoal100 } : {}),
        }}
      >
        {renderFallbackIcon?.({ size: Theme.spacing.M })}
      </Avatar>
    ),
  };

  const { renderedLabel, label, renderedSubtext, subtext } =
    processMenuItemText(itemProps.item, inputValue);

  const text = (
    <OverflowTooltip
      content={label}
      placement={OverflowTooltip.PLACEMENTS.TOP}
      data-testid="AssigneePicker--textTooltip"
    >
      {renderedLabel}
    </OverflowTooltip>
  );
  if (typeof subtext !== 'string') {
    return (
      <AssigneePickerVirtualizedMenuItem
        {...iconProps}
        {...restProps}
        start={restProps.start ?? 0}
        height={restProps.height ?? 0}
        text={text}
      />
    );
  }

  return (
    <AssigneePickerVirtualizedMenuItem
      {...iconProps}
      {...restProps}
      start={restProps.start ?? 0}
      height={restProps.height ?? 0}
      text={text}
      subtext={
        <OverflowTooltip
          content={subtext}
          placement={OverflowTooltip.PLACEMENTS.TOP}
          data-testid="AssigneePicker--subtextTooltip"
        >
          {renderedSubtext}
        </OverflowTooltip>
      }
    />
  );
};
